<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Header -->
    <div class="bg-white dark:bg-gray-800 shadow">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="py-6">
          <div class="flex items-center justify-between">
            <div>
              <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">Preferences</h1>
              <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Customize your experience and account settings
              </p>
            </div>
            <div class="flex items-center space-x-3">
              <button
                :disabled="!hasChanges || saving"
                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
                @click="savePreferences"
              >
                <span v-if="saving" class="flex items-center">
                  <svg
                    class="animate-spin -ml-1 mr-3 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Saving...
                </span>
                <span v-else>Save Changes</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="space-y-6">
        <!-- General Preferences -->
        <div class="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100">General Settings</h2>
          </div>
          <div class="p-6 space-y-6">
            <!-- Language -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Language
              </label>
              <select
                v-model="preferences.language"
                class="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="en">English</option>
                <option value="es">Español</option>
                <option value="fr">Français</option>
                <option value="de">Deutsch</option>
                <option value="it">Italiano</option>
                <option value="pt">Português</option>
                <option value="ru">Русский</option>
                <option value="zh">中文</option>
                <option value="ja">日本語</option>
              </select>
            </div>

            <!-- Timezone -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Timezone
              </label>
              <select
                v-model="preferences.timezone"
                class="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="UTC">UTC (Coordinated Universal Time)</option>
                <option value="America/New_York">Eastern Time (US & Canada)</option>
                <option value="America/Chicago">Central Time (US & Canada)</option>
                <option value="America/Denver">Mountain Time (US & Canada)</option>
                <option value="America/Los_Angeles">Pacific Time (US & Canada)</option>
                <option value="Europe/London">London</option>
                <option value="Europe/Paris">Paris</option>
                <option value="Europe/Berlin">Berlin</option>
                <option value="Europe/Rome">Rome</option>
                <option value="Asia/Tokyo">Tokyo</option>
                <option value="Asia/Shanghai">Shanghai</option>
                <option value="Asia/Mumbai">Mumbai</option>
                <option value="Australia/Sydney">Sydney</option>
              </select>
            </div>

            <!-- Date Format -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Date Format
              </label>
              <select
                v-model="preferences.dateFormat"
                class="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="MM/DD/YYYY">MM/DD/YYYY (US)</option>
                <option value="DD/MM/YYYY">DD/MM/YYYY (UK)</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD (ISO)</option>
                <option value="DD.MM.YYYY">DD.MM.YYYY (German)</option>
              </select>
            </div>

            <!-- Time Format -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Time Format
              </label>
              <div class="space-y-2">
                <label class="flex items-center">
                  <input
                    v-model="preferences.timeFormat"
                    type="radio"
                    value="12"
                    class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                  />
                  <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">12-hour (1:30 PM)</span>
                </label>
                <label class="flex items-center">
                  <input
                    v-model="preferences.timeFormat"
                    type="radio"
                    value="24"
                    class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                  />
                  <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">24-hour (13:30)</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Notification Preferences -->
        <div class="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100">Notifications</h2>
          </div>
          <div class="p-6 space-y-6">
            <!-- Email Notifications -->
            <div>
              <label class="flex items-center justify-between">
                <div>
                  <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Email Notifications</span>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Receive notifications via email
                  </p>
                </div>
                <input
                  v-model="preferences.notifications.email"
                  type="checkbox"
                  class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
              </label>
            </div>

            <!-- Telegram Notifications -->
            <div>
              <label class="flex items-center justify-between">
                <div>
                  <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Telegram Notifications</span>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Receive notifications via Telegram bot
                  </p>
                </div>
                <input
                  v-model="preferences.notifications.telegram"
                  type="checkbox"
                  class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
              </label>
            </div>

            <!-- Marketing Communications -->
            <div>
              <label class="flex items-center justify-between">
                <div>
                  <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Marketing Communications</span>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Receive updates about new features and promotions
                  </p>
                </div>
                <input
                  v-model="preferences.notifications.marketing"
                  type="checkbox"
                  class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
              </label>
            </div>

            <!-- Security Alerts -->
            <div>
              <label class="flex items-center justify-between">
                <div>
                  <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Security Alerts</span>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Important security notifications (recommended)
                  </p>
                </div>
                <input
                  v-model="preferences.notifications.security"
                  type="checkbox"
                  class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
              </label>
            </div>
          </div>
        </div>

        <!-- Interface Preferences -->
        <div class="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100">Interface</h2>
          </div>
          <div class="p-6 space-y-6">
            <!-- Theme -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Theme
              </label>
              <div class="space-y-2">
                <label class="flex items-center">
                  <input
                    v-model="preferences.theme"
                    type="radio"
                    value="light"
                    class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                  />
                  <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Light</span>
                </label>
                <label class="flex items-center">
                  <input
                    v-model="preferences.theme"
                    type="radio"
                    value="dark"
                    class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                  />
                  <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Dark</span>
                </label>
                <label class="flex items-center">
                  <input
                    v-model="preferences.theme"
                    type="radio"
                    value="auto"
                    class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                  />
                  <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Auto (system preference)</span>
                </label>
              </div>
            </div>

            <!-- Compact Mode -->
            <div>
              <label class="flex items-center justify-between">
                <div>
                  <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Compact Mode</span>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Use smaller spacing and elements
                  </p>
                </div>
                <input
                  v-model="preferences.compactMode"
                  type="checkbox"
                  class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
              </label>
            </div>

            <!-- Animations -->
            <div>
              <label class="flex items-center justify-between">
                <div>
                  <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Animations</span>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Enable interface animations and transitions
                  </p>
                </div>
                <input
                  v-model="preferences.animations"
                  type="checkbox"
                  class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
              </label>
            </div>
          </div>
        </div>

        <!-- Success Message -->
        <div
          v-if="showSuccessMessage"
          class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-4"
        >
          <div class="flex">
            <div class="flex-shrink-0">
              <CheckIcon class="h-5 w-5 text-green-400" aria-hidden="true" />
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium text-green-800 dark:text-green-200">
                Preferences saved successfully!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { CheckIcon } from '@heroicons/vue/24/outline';

defineOptions({
  name: 'PreferencesView',
});

const router = useRouter();
const authStore = useAuthStore();

// State
const saving = ref(false);
const showSuccessMessage = ref(false);

// Preferences data
const preferences = ref({
  language: 'en',
  timezone: 'UTC',
  dateFormat: 'MM/DD/YYYY',
  timeFormat: '12',
  theme: 'auto',
  compactMode: false,
  animations: true,
  notifications: {
    email: true,
    telegram: true,
    marketing: false,
    security: true,
  },
});

// Original preferences for change detection
const originalPreferences = ref({});

// Computed
const hasChanges = computed(() => {
  return JSON.stringify(preferences.value) !== JSON.stringify(originalPreferences.value);
});

// Methods
const loadPreferences = () => {
  // Load preferences from user profile or defaults
  const userPreferences = (authStore.user?.preferences || {}) as any;

  preferences.value = {
    language: userPreferences.language || 'en',
    timezone: userPreferences.timezone || 'UTC',
    dateFormat: userPreferences.dateFormat || 'MM/DD/YYYY',
    timeFormat: userPreferences.timeFormat || '12',
    theme: userPreferences.theme || 'auto',
    compactMode: userPreferences.compactMode || false,
    animations: userPreferences.animations !== false, // Default to true
    notifications: {
      email: userPreferences.notifications?.email !== false,
      telegram: userPreferences.notifications?.telegram !== false,
      marketing: userPreferences.notifications?.marketing || false,
      security: userPreferences.notifications?.security !== false,
    },
  };

  // Store original for change detection
  originalPreferences.value = JSON.parse(JSON.stringify(preferences.value));
};

const savePreferences = async () => {
  if (!hasChanges.value || saving.value) return;

  saving.value = true;
  showSuccessMessage.value = false;

  try {
    // Here you would call the API to save preferences
    // await tierApi.updateUserPreferences(preferences.value)

    // For now, just simulate the API call
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Update the original preferences
    originalPreferences.value = JSON.parse(JSON.stringify(preferences.value));

    // Show success message
    showSuccessMessage.value = true;
    setTimeout(() => {
      showSuccessMessage.value = false;
    }, 3000);

    // Apply theme change immediately if changed
    if (preferences.value.theme !== (originalPreferences.value as any).theme) {
      applyTheme(preferences.value.theme);
    }

    console.log('Preferences saved:', preferences.value);
  } catch (error) {
    console.error('Failed to save preferences:', error);
    // Handle error - show error message
  } finally {
    saving.value = false;
  }
};

const applyTheme = (theme: string) => {
  const root = document.documentElement;

  if (theme === 'dark') {
    root.classList.add('dark');
  } else if (theme === 'light') {
    root.classList.remove('dark');
  } else if (theme === 'auto') {
    // Use system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (prefersDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }
};

// Watch for theme changes and apply immediately
watch(
  () => preferences.value.theme,
  newTheme => {
    applyTheme(newTheme);
  }
);

// Lifecycle
onMounted(() => {
  if (!authStore.isAuthenticated) {
    router.push('/auth');
    return;
  }

  loadPreferences();
});

// Handle beforeunload to warn about unsaved changes
window.addEventListener('beforeunload', e => {
  if (hasChanges.value) {
    e.preventDefault();
    e.returnValue = '';
  }
});
</script>

<style scoped>
/* Custom styling for form elements */
input[type='checkbox'],
input[type='radio'] {
  @apply focus:ring-blue-500 text-blue-600 border-gray-300 dark:border-gray-600;
}

select {
  @apply focus:ring-blue-500 focus:border-blue-500;
}

/* Smooth transitions */
.transition-all {
  transition: all 0.2s ease-in-out;
}

/* Custom scrollbar for this page */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: theme('colors.gray.300');
  border-radius: 3px;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: theme('colors.gray.600');
}
</style>
